import {
  fetchLeaveBalances,
  fetchLeaveRequests,
} from "../leave/leaveApi";
import { formatIndianDate } from "../attendance/indianCalendarUtils";
import { employeeFullName } from "./employeeReportsUtils";

const CORE_TYPES = ["CL", "SL", "EL"];

const CODE_COL_LABELS = {
  CL: "CL",
  SL: "SL",
  EL: "EL",
};

function leaveTypeColumnLabel(leaveType) {
  const code = (leaveType?.code || "").toUpperCase();
  return CODE_COL_LABELS[code] || leaveType?.name || leaveType?.code || "Balance";
}

/** The organisation's own active leave type codes, in their configured order. */
function orgLeaveTypeCodes(leaveTypes) {
  const codes = [];
  (leaveTypes || []).forEach((t) => {
    const code = (t?.code || "").toUpperCase();
    if (code && !codes.includes(code)) codes.push(code);
  });
  return codes;
}

export function getLeaveBalanceReportColumns(leaveTypeId, leaveTypes) {
  if (!leaveTypeId) {
    // One column per leave type the organisation actually has (no fixed CL/SL/EL).
    return ["Employee", ...orgLeaveTypeCodes(leaveTypes), "Balance"];
  }
  const lt = (leaveTypes || []).find((t) => String(t.leave_type_id) === String(leaveTypeId));
  const typeCol = leaveTypeColumnLabel(lt);
  return ["Employee", typeCol, "Balance"];
}

function buildBalanceRow(emp, pivot, leaveTypeId, leaveTypes) {
  const employee = employeeFullName(emp);

  if (!leaveTypeId) {
    const row = { Employee: employee };
    let total = 0;
    orgLeaveTypeCodes(leaveTypes).forEach((code) => {
      const available = balanceAvailable(pivot[code]);
      row[code] = available;
      total += available;
    });
    row.Balance = total;
    return row;
  }

  const lt = (leaveTypes || []).find((t) => String(t.leave_type_id) === String(leaveTypeId));
  const code = (lt?.code || "").toUpperCase();
  const typeCol = leaveTypeColumnLabel(lt);
  const balance = code ? balanceAvailable(pivot[code]) : 0;

  return {
    Employee: employee,
    [typeCol]: balance,
    Balance: balance,
  };
}

function pivotBalances(rows) {
  const map = {};
  (rows || []).forEach((b) => {
    const code = (b.leave_type_code || "").toUpperCase();
    map[code] = b;
  });
  return map;
}

function balanceAvailable(b) {
  return Number(b?.available ?? 0);
}

export function formatLeaveStatus(status) {
  if (!status) return "—";
  const s = String(status).toLowerCase();
  if (s === "pending" || s === "escalated") return "Pending";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function filterEmployeesByDepartment(employees, departmentId) {
  if (!departmentId) return employees || [];
  return (employees || []).filter(
    (e) => String(e.department_id ?? e.department?.id ?? "") === String(departmentId)
  );
}

function inDateRange(req, fromDate, toDate) {
  if (fromDate && req.end_date < fromDate) return false;
  if (toDate && req.start_date > toDate) return false;
  return true;
}

export async function buildLeaveBalanceReportRows({
  orgId,
  year,
  departmentId,
  leaveTypeId,
  employees,
  leaveTypes,
}) {
  const filtered = filterEmployeesByDepartment(employees, departmentId);
  const batch = await Promise.all(
    filtered.map(async (emp) => {
      const balances = await fetchLeaveBalances({
        organisation_id: orgId,
        employee_id: emp.employee_id,
        year,
      }).catch(() => []);
      const pivot = pivotBalances(balances);
      return buildBalanceRow(emp, pivot, leaveTypeId, leaveTypes);
    })
  );

  return batch;
}

export async function buildLeaveRegisterRows({
  orgId,
  employeeId,
  fromDate,
  toDate,
  leaveTypeId,
  employees,
}) {
  const requests = await fetchLeaveRequests({
    organisation_id: orgId,
    employee_id: employeeId || undefined,
  });
  const empMap = new Map((employees || []).map((e) => [String(e.employee_id), e]));

  return (requests || [])
    .filter((req) => inDateRange(req, fromDate, toDate))
    .filter((req) => !leaveTypeId || String(req.leave_type_id) === String(leaveTypeId))
    .map((req) => {
      const emp = empMap.get(String(req.employee_id));
      return {
        Employee: req.employee_name || employeeFullName(emp) || "—",
        "Leave Type": req.leave_type_name || req.leave_type_code || "—",
        "From Date": formatIndianDate(req.start_date),
        "To Date": formatIndianDate(req.end_date),
        Status: formatLeaveStatus(req.status),
      };
    });
}

export async function buildLeaveUtilizationRows({ orgId, year, departmentId, employees }) {
  const filtered = filterEmployeesByDepartment(employees, departmentId);
  const deptAgg = {};

  await Promise.all(
    filtered.map(async (emp) => {
      const dept = emp.department?.name || emp.department_name || "Unassigned";
      const balances = await fetchLeaveBalances({
        organisation_id: orgId,
        employee_id: emp.employee_id,
        year,
      }).catch(() => []);

      let availed = 0;
      let remaining = 0;
      for (const b of balances) {
        const code = (b.leave_type_code || "").toUpperCase();
        if (!CORE_TYPES.includes(code)) continue;
        availed += Number(b.consumed ?? 0);
        remaining += Number(b.available ?? 0);
      }

      if (!deptAgg[dept]) {
        deptAgg[dept] = { availed: 0, remaining: 0 };
      }
      deptAgg[dept].availed += availed;
      deptAgg[dept].remaining += remaining;
    })
  );

  return Object.entries(deptAgg)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([dept, vals]) => ({
      Department: dept,
      "Leaves Availed": vals.availed,
      "Leaves Remaining": vals.remaining,
    }));
}

export async function buildPendingLeaveRows({ orgId, departmentId, approverRole, employees }) {
  const requests = await fetchLeaveRequests({ organisation_id: orgId });
  const empMap = new Map((employees || []).map((e) => [String(e.employee_id), e]));

  return (requests || [])
    .filter((req) => {
      const status = String(req.status || "").toLowerCase();
      return status === "pending" || status === "escalated";
    })
    .filter((req) => {
      const emp = empMap.get(String(req.employee_id));
      if (departmentId) {
        const deptId = String(emp?.department_id ?? emp?.department?.id ?? "");
        if (deptId !== String(departmentId)) return false;
      }
      if (approverRole && req.pending_approver_role !== approverRole) return false;
      return true;
    })
    .map((req) => {
      const emp = empMap.get(String(req.employee_id));
      return {
        Employee: req.employee_name || employeeFullName(emp) || "—",
        "Leave Type": req.leave_type_name || req.leave_type_code || "—",
        "From Date": formatIndianDate(req.start_date),
        Days: req.total_days ?? "—",
        Status: "Pending",
      };
    });
}
